import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase, Induction, Site, Worker } from '../lib/supabase';
import { InductionForm } from '../components/forms/InductionForm';
import { generateInductionPDF } from '../utils/pdfGenerator';
import { 
  ClipboardCheck, 
  Edit, 
  ArrowLeft,
  Building2,
  Users,
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  PenTool,
  Shield,
  FileText,
  Download
} from 'lucide-react';

interface InductionWithDetails extends Induction {
  site?: Site;
  worker?: Worker;
}

export const InductionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [induction, setInduction] = useState<InductionWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleDownloadInduction = () => {
    if (!induction || induction.status !== 'completed') return;
    
    // Generate and download PDF certificate
    generateInductionPDF(induction);
  };

  useEffect(() => {
    if (id) {
      fetchInductionDetails();
    }
  }, [id]);

  const fetchInductionDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('inductions')
        .select(`
          *,
          sites!inner(
            id, name, type, address, start_date, end_date, 
            description, requirements, photo_url, status, 
            asbestos_type, created_at, updated_at
          ),
          workers!inner(
            id, name, role, email, phone, start_date, 
            certifications, status, whitecard_number, 
            created_at, updated_at
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      setInduction({
        ...data,
        site: data.sites,
        worker: data.workers
      });
    } catch (error) {
      console.error('Error fetching induction details:', error);
    } finally {
      setLoading(false);
    }
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-6 w-6 text-green-600" />;
      case 'pending':
        return <Clock className="h-6 w-6 text-yellow-600" />;
      case 'expired':
        return <AlertTriangle className="h-6 w-6 text-red-600" />;
      default:
        return <ClipboardCheck className="h-6 w-6 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!induction) {
    return (
      <div className="text-center py-12">
        <ClipboardCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Induction not found</h3>
        <Link to="/inductions" className="text-orange-600 hover:text-orange-700">
          Return to Inductions
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/inductions')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              {getStatusIcon(induction.status)}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Site Induction</h1>
              <p className="text-gray-600">
                {induction.worker?.name} • {induction.site?.name}
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowEditModal(true)}
          className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 transition-colors"
        >
          <Edit className="h-5 w-5" />
          <span>Edit Induction</span>
        </button>
      </div>

      {/* Status Banner */}
      <div className={`rounded-lg p-4 ${
        induction.status === 'completed' ? 'bg-green-50 border border-green-200' :
        induction.status === 'pending' ? 'bg-yellow-50 border border-yellow-200' :
        'bg-red-50 border border-red-200'
      }`}>
        <div className="flex items-center space-x-3">
          {getStatusIcon(induction.status)}
          <div>
            <h3 className={`font-medium ${
              induction.status === 'completed' ? 'text-green-900' :
              induction.status === 'pending' ? 'text-yellow-900' :
              'text-red-900'
            }`}>
              Induction {induction.status.charAt(0).toUpperCase() + induction.status.slice(1)}
            </h3>
            <p className={`text-sm ${
              induction.status === 'completed' ? 'text-green-700' :
              induction.status === 'pending' ? 'text-yellow-700' :
              'text-red-700'
            }`}>
              {induction.status === 'completed' && induction.completed_at && 
                `Completed on ${new Date(induction.completed_at).toLocaleDateString()}`}
              {induction.status === 'pending' && 
                'Awaiting completion of safety induction process'}
              {induction.status === 'expired' && 
                'Induction has expired and needs to be renewed'}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Induction Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Induction Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(induction.status)}`}>
                  {induction.status.charAt(0).toUpperCase() + induction.status.slice(1)}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <p className="text-sm text-gray-900">
                    {new Date(induction.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {induction.completed_at && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Completed Date</label>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <p className="text-sm text-gray-900">
                      {new Date(induction.completed_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                <p className="text-sm text-gray-900">
                  {new Date(induction.updated_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            {induction.signature && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Digital Signature</label>
                <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
                  <div className="flex items-center space-x-2 mb-3">
                    <PenTool className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Worker Acknowledgment</span>
                  </div>
                  <div className="bg-white rounded-md p-4 border border-gray-200 min-h-[80px] flex items-center justify-center">
                    {induction.signature.startsWith('data:image') ? (
                      <img 
                        src={induction.signature} 
                        alt="Worker signature" 
                        className="max-w-full h-auto max-h-20"
                      />
                    ) : (
                      <p className="text-lg text-gray-900 font-serif italic text-center leading-relaxed">
                        {induction.signature}
                      </p>
                    )}
                  </div>
                  <div className="mt-3 pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500 text-center">
                      Digitally signed on {induction.completed_at ? new Date(induction.completed_at).toLocaleString() : 'N/A'}
                    </p>
                    <p className="text-xs text-gray-500 text-center mt-1">
                      This signature confirms the worker has completed the safety induction and understands all requirements
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Worker Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Worker Information</h3>
            {induction.worker && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">{induction.worker.name}</h4>
                    <p className="text-sm text-gray-600 capitalize">{induction.worker.role.replace('_', ' ')}</p>
                    <p className="text-sm text-gray-500">{induction.worker.email}</p>
                    {induction.worker.phone && (
                      <p className="text-sm text-gray-500">{induction.worker.phone}</p>
                    )}
                  </div>
                </div>
                <Link
                  to={`/workers/${induction.worker.id}`}
                  className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                >
                  View Worker Profile
                </Link>
              </div>
            )}

            {induction.worker?.certifications && induction.worker.certifications.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Worker Certifications</label>
                <div className="flex flex-wrap gap-2">
                  {induction.worker.certifications.map((cert, index) => (
                    <span key={index} className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Site Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Site Information</h3>
            {induction.site && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">{induction.site.name}</h4>
                    <p className="text-sm text-gray-600 capitalize">{induction.site.type.replace('_', ' ')}</p>
                    <p className="text-sm text-gray-500">{induction.site.address}</p>
                  </div>
                </div>
                <Link
                  to={`/sites/${induction.site.id}`}
                  className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                >
                  View Site Details
                </Link>
              </div>
            )}

            {induction.site?.requirements && induction.site.requirements.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Site Safety Requirements</label>
                <div className="flex flex-wrap gap-2">
                  {induction.site.requirements.map((req, index) => (
                    <span key={index} className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">
                      {req}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {induction.site?.description && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Site Description</label>
                <p className="text-sm text-gray-900">{induction.site.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Safety Checklist */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Safety Checklist</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">Site hazards explained</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">Emergency procedures covered</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">PPE requirements verified</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">Work methods reviewed</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-700">Site rules explained</span>
              </div>
            </div>
          </div>

          {/* Compliance Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Compliance Status</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-800">Safety Compliant</span>
              </div>
              {induction.status === 'completed' && (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-green-800">Induction Complete</span>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <span className="text-sm text-blue-800">Documentation Current</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button 
                onClick={handleDownloadInduction}
                disabled={induction.status !== 'completed'}
                className="block w-full text-left p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Generate Certificate</p>
                    <p className="text-xs text-green-600">
                      {induction.status === 'completed' ? 'Download completion certificate' : 'Available when completed'}
                    </p>
                  </div>
                </div>
              </button>
              <Link
                to="/reports"
                className="block w-full text-left p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-blue-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">View Reports</p>
                    <p className="text-xs text-blue-600">Safety compliance reports</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <InductionForm
          induction={induction}
          onSubmit={handleUpdateInduction}
          onClose={() => setShowEditModal(false)}
          loading={submitting}
        />
      )}
    </div>
  );
};